/**
 * Customer Perception Insights Styles
 * Using plain objects for MUI sx prop compatibility
 */

import { SxProps, Theme } from "@mui/material";
import { THEME } from "./CustomerPerception.constants";

// Type for sx-compatible style objects
type StyleObject = SxProps<Theme>;

export const CUSTOMER_PERCEPTION_STYLES: Record<string, StyleObject> = {
    root: {
        padding: "12px",
        background: THEME.background,
        minHeight: "100vh"
    },
    
    // Page Header
    header: {
        background: THEME.primary,
        color: "#FFFFFF",
        padding: "20px 24px",
        borderRadius: "12px",
        marginBottom: "16px"
    },
    headerTitle: {
        fontSize: "24px",
        fontWeight: 700,
        marginBottom: "4px",
        display: "flex",
        alignItems: "center",
        gap: "12px"
    },
    headerSubtitle: {
        fontSize: "14px",
        fontWeight: 400,
        color: "rgba(255,255,255,0.85)"
    },
    headerBreadcrumb: {
        fontSize: "12px",
        color: "rgba(255,255,255,0.7)",
        marginBottom: "8px"
    },
    dataAsOf: {
        position: "absolute",
        top: "20px",
        right: "24px",
        background: "rgba(0,0,0,0.2)",
        padding: "6px 12px",
        borderRadius: "16px",
        fontSize: "12px",
        color: "rgba(255,255,255,0.9)"
    },
    
    // Work in Progress Banner
    wipBanner: {
        background: "#FEF3C7",
        border: "1px solid #F59E0B",
        borderRadius: "8px",
        padding: "12px 16px",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },
    wipText: {
        fontSize: "13px",
        color: "#92400E"
    },
    
    // Narrative Box
    narrativeBox: {
        background: THEME.primaryLight,
        border: `1px solid ${THEME.primary}`,
        borderLeft: `4px solid ${THEME.primary}`,
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "16px"
    },
    narrativeText: {
        fontSize: "14px",
        color: THEME.textPrimary,
        lineHeight: 1.6
    },
    narrativeHighlight: {
        fontWeight: 600,
        color: THEME.primary
    },
    
    // KPI Tiles Row
    kpiRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "20px"
    },
    kpiTile: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "16px 24px",
        background: `linear-gradient(135deg, ${THEME.surface} 0%, #F8FAFC 100%)`,
        borderRadius: "12px",
        border: `1px solid ${THEME.border}`,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        position: "relative",
        overflow: "hidden",
        minWidth: "140px",
        transition: "all 0.2s ease",
        "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-1px)"
        }
    },
    kpiAccent: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: `linear-gradient(90deg, ${THEME.primary} 0%, #60A5FA 100%)`,
        borderRadius: "12px 12px 0 0"
    },
    kpiValue: {
        fontSize: "36px",
        fontWeight: 700,
        color: THEME.textPrimary,
        lineHeight: 1,
        marginTop: "4px"
    },
    kpiLabel: {
        fontSize: "11px",
        fontWeight: 600,
        color: THEME.textSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginTop: "6px"
    },
    
    // Tab Navigation
    tabContainer: {
        display: "flex",
        gap: "8px",
        marginBottom: "16px"
    },
    tab: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "12px 20px",
        background: THEME.surface,
        border: `1px solid ${THEME.border}`,
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    tabActive: {
        background: THEME.primary,
        borderColor: THEME.primary,
        color: "#FFFFFF"
    },
    tabNumber: {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 600
    },
    tabText: {
        fontSize: "14px",
        fontWeight: 500
    },
    tabSubtext: {
        fontSize: "11px",
        opacity: 0.7
    },
    
    // Content Card
    contentCard: {
        background: THEME.surface,
        borderRadius: "12px",
        border: `1px solid ${THEME.border}`,
        padding: "20px",
        marginBottom: "16px"
    },
    cardTitle: {
        fontSize: "16px",
        fontWeight: 600,
        color: THEME.textPrimary,
        marginBottom: "4px"
    },
    cardSubtitle: {
        fontSize: "13px",
        color: THEME.textSecondary,
        marginBottom: "16px"
    },
    
    // How To Read Box
    howToReadBox: {
        background: "#F8FAFC",
        border: `1px solid ${THEME.border}`,
        borderRadius: "8px",
        padding: "12px 16px",
        marginBottom: "20px"
    },
    howToReadLabel: {
        fontSize: "12px",
        fontWeight: 600,
        color: THEME.textSecondary,
        marginRight: "8px"
    },
    howToReadText: {
        fontSize: "13px",
        color: THEME.textPrimary,
        lineHeight: 1.5
    }
};

export const FLOW_RIBBONS_STYLES: Record<string, StyleObject> = {
    container: {
        padding: "0"
    },
    sankeyContainer: {
        width: "100%",
        overflowX: "auto",
        paddingBottom: "16px"
    },
    
    // Legend
    legend: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        padding: "6px 10px",
        background: "#F8FAFC",
        borderRadius: "6px",
        marginTop: "8px"
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: "4px"
    },
    legendDot: {
        width: "7px",
        height: "7px",
        borderRadius: "50%"
    },
    legendText: {
        fontSize: "9px",
        color: THEME.textSecondary
    },
    
    // Customer Chips Section
    chipsSection: {
        marginTop: "16px",
        padding: "12px",
        background: THEME.surface,
        borderRadius: "10px",
        border: `1px solid ${THEME.border}`
    },
    chipsHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "10px"
    },
    chipsTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: THEME.textPrimary
    },
    chipsGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px"
    },
    customerChip: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        background: THEME.surface,
        border: `1px solid ${THEME.border}`,
        borderRadius: "20px",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    customerChipSelected: {
        background: THEME.primaryLight,
        borderColor: THEME.primary
    },
    chipName: {
        fontSize: "13px",
        fontWeight: 500,
        color: THEME.textPrimary
    },
    chipPM: {
        fontSize: "11px",
        color: THEME.textSecondary
    },
    chipIndicator: {
        width: "8px",
        height: "8px",
        borderRadius: "50%"
    }
};

export const JOURNEY_BOARD_STYLES: Record<string, StyleObject> = {
    container: {
        padding: "0"
    },
    
    // Funnel Cards
    funnelRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "24px"
    },
    funnelCard: {
        flex: 1,
        padding: "16px 20px",
        borderRadius: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "2px solid transparent"
    },
    funnelCardSelected: {
        borderColor: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
    },
    funnelValue: {
        fontSize: "36px",
        fontWeight: 700,
        color: "#FFFFFF",
        lineHeight: 1
    },
    funnelLabel: {
        fontSize: "14px",
        fontWeight: 500,
        color: "rgba(255,255,255,0.95)",
        marginTop: "4px"
    },
    funnelPercent: {
        fontSize: "12px",
        color: "rgba(255,255,255,0.75)",
        marginTop: "4px"
    },
    
    // Executive Read
    executiveRead: {
        background: THEME.primaryLight,
        border: `1px solid ${THEME.primary}`,
        borderLeft: `4px solid ${THEME.primary}`,
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "20px"
    },
    executiveReadLabel: {
        fontSize: "12px",
        fontWeight: 600,
        color: THEME.primary,
        marginBottom: "8px"
    },
    executiveReadText: {
        fontSize: "14px",
        color: THEME.textPrimary,
        lineHeight: 1.6
    },
    highlightBlue: {
        fontWeight: 600,
        color: THEME.primary,
        textDecoration: "underline"
    },
    
    // Panels Grid
    panelsGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
        marginBottom: "24px"
    },
    panel: {
        background: THEME.surface,
        borderRadius: "12px",
        border: `1px solid ${THEME.border}`,
        padding: "16px"
    },
    panelTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: THEME.textPrimary,
        marginBottom: "12px"
    },
    barRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "8px"
    },
    barLabel: {
        flex: "0 0 140px",
        fontSize: "12px",
        color: THEME.textSecondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    barContainer: {
        flex: 1,
        height: "20px",
        background: THEME.borderLight,
        borderRadius: "4px",
        overflow: "hidden"
    },
    bar: {
        height: "100%",
        borderRadius: "4px",
        transition: "width 0.4s ease"
    },
    barValue: {
        flex: "0 0 60px",
        fontSize: "12px",
        fontWeight: 500,
        color: THEME.textPrimary,
        textAlign: "right"
    },
    
    // Live Preview Usage
    usageSection: {
        background: THEME.surface,
        borderRadius: "12px",
        border: `1px solid ${THEME.border}`,
        padding: "16px",
        marginBottom: "24px"
    },
    usageHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px"
    },
    usageTitle: {
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },
    usageTitleText: {
        fontSize: "14px",
        fontWeight: 600,
        color: THEME.textPrimary
    },
    filteredBadge: {
        background: THEME.success,
        color: "#FFFFFF",
        fontSize: "10px",
        fontWeight: 600,
        padding: "4px 8px",
        borderRadius: "4px",
        textTransform: "uppercase"
    },
    usageSubtitle: {
        fontSize: "12px",
        color: THEME.textSecondary,
        marginBottom: "16px"
    },
    usageTiles: {
        display: "flex",
        gap: "16px"
    },
    usageTile: {
        flex: 1,
        textAlign: "center"
    },
    usageTileValue: {
        fontSize: "28px",
        fontWeight: 700,
        color: THEME.textPrimary
    },
    usageTileLabel: {
        fontSize: "11px",
        color: THEME.textSecondary,
        textTransform: "uppercase"
    },
    
    // Footer
    footer: {
        textAlign: "center",
        fontSize: "12px",
        color: THEME.textMuted,
        marginTop: "24px",
        paddingTop: "16px",
        borderTop: `1px solid ${THEME.border}`
    }
};
